import { type Bar, type HistoryMetadata } from 'charting_library';

import { SystemError } from '#/shared/exceptions/system-error';

import { type CandlesUdfResponse, type TicksUdfResponse } from '#/types/udf.types';

export const udfResponseToBarsResult = (
  udfResponse: CandlesUdfResponse | TicksUdfResponse
): { bars: Bar[]; meta?: HistoryMetadata } => {
  if (udfResponse.s === 'error') {
    throw new SystemError('History bars response with error.', { udfResponse });
  }

  if (udfResponse.s === 'no_data') {
    return {
      bars: [],
      meta: {
        noData: true,
        nextTime: udfResponse.nextTime,
      },
    };
  }

  if (udfResponse.s === 'ok') {
    const bars: Bar[] = [];

    const getIsCandlesUdfResponse = (response: CandlesUdfResponse | TicksUdfResponse): response is CandlesUdfResponse =>
      ['l', 'h', 'o', 'c'].every((key) => key in response);

    const getIsTicksUdfResponse = (response: CandlesUdfResponse | TicksUdfResponse): response is TicksUdfResponse =>
      ['a', 'b'].every((key) => key in response);

    const isCandlesResponse = getIsCandlesUdfResponse(udfResponse);
    const isTicksUdfResponse = getIsTicksUdfResponse(udfResponse);

    if (!isCandlesResponse && !isTicksUdfResponse) {
      throw new SystemError('Incorrect UDF response. Mismatch of candlesUdfResponse or ticksUdfResponse.', {
        udfResponse,
      });
    }

    if (isCandlesResponse) {
      const lowLength = udfResponse.l.length;
      const highLength = udfResponse.h.length;
      const timeLength = udfResponse.t.length;
      const openLength = udfResponse.o.length;
      const closeLength = udfResponse.c.length;
      const volumeLength = Array.isArray(udfResponse.v) ? udfResponse.v.length : udfResponse.t;

      if (!(
        lowLength === highLength &&
        highLength === timeLength &&
        timeLength === openLength &&
        openLength === closeLength &&
        closeLength === volumeLength
      )) {
        throw new SystemError('Arrays of history bars has not equal length.', { udfResponse });
      }

      // Time should be seconds timestamp
      udfResponse.t.forEach((time, i) => {
        const bar: Bar = {
          time: time * 1000, // TradingView requires time in milliseconds
          open: udfResponse.o[i],
          close: udfResponse.c[i],
          low: udfResponse.l[i],
          high: udfResponse.h[i],
        };

        if (Array.isArray(udfResponse.v)) {
          bar.volume = udfResponse.v[i];
        }

        bars.push(bar);
      });
    }

    if (isTicksUdfResponse) {
      const askLength = udfResponse.a.length;
      const bidLength = udfResponse.b.length;
      const timeLength = udfResponse.t.length;

      if (!(askLength === bidLength && bidLength === timeLength && timeLength)) {
        throw new SystemError('Arrays of history bars has not equal length.', { udfResponse });
      }

      // time should be milliseconds timestamp
      udfResponse.t.forEach((time, i) => {
        const bar: Bar = {
          time: time, // TradingView requires time in milliseconds
          open: udfResponse.a[i],
          close: udfResponse.b[i],
          low: udfResponse.a[i],
          high: udfResponse.b[i],
        };

        bars.push(bar);
      });
    }

    return {
      bars,
    };
  }

  throw new SystemError('Incorrect status of getBars response.', { udfResponse });
};
